/** @file proposeTargets: which part each island of an exploded sheet most likely is, by layout. */
import type { Island } from '@zilo/pose3d-image-io/island/islandTypes';
import { assignMinCost } from './assignMinCost';
import { pivotProposal } from './pivotProposal';
import { primaryPartsOf } from './primaryPartsOf';
import type { PlacedPart, Point, RestPlacement } from './sheetTypes';

/** One island and the part it fills (null = unused), with the pivot in island pixels as the island is seen. */
export type IslandTarget = { island: Island; part: PlacedPart | null; pivot: Point };

/** An island farther than this (in the normalised layout) from every part stays unused. */
const UNUSED = 0.45;

/** A box as `[x0, y0, x1, y1]`. */
type Box = [number, number, number, number];

/** Maps points into the unit square of their own box (a flat axis maps to 0.5). */
function normaliser(points: readonly Point[]): (p: Point) => Point {
  const box: Box = [Math.min(...points.map((p) => p[0])), Math.min(...points.map((p) => p[1])), Math.max(...points.map((p) => p[0])), Math.max(...points.map((p) => p[1]))];
  const axis = (v: number, a: number, b: number): number => (b - a < 1e-9 ? 0.5 : (v - a) / (b - a));
  return (p) => [axis(p[0], box[0], box[2]), axis(p[1], box[1], box[3])];
}

/** The centre of an island's box and of a part's quad. */
const islandCentre = (i: Island): Point => [i.x + i.w / 2, i.y + i.h / 2];
const partCentre = (p: PlacedPart): Point => [p.quad.reduce((s, q) => s + q[0], 0) / 4, p.quad.reduce((s, q) => s + q[1], 0) / 4];

/** The cheapest assignment of islands to body parts (squared layout distance; `UNUSED` for leaving an island out). */
function assignBody(islands: readonly Island[], body: readonly PlacedPart[]): (PlacedPart | null)[] {
  const ni = normaliser(islands.flatMap((i) => [[i.x, i.y], [i.x + i.w, i.y + i.h]] as Point[]));
  const np = normaliser(body.flatMap((p) => p.quad));
  const a = islands.map((i) => ni(islandCentre(i)));
  const b = body.map((p) => np(partCentre(p)));
  const n = islands.length + body.length;
  const cost = (r: number, c: number): number => {
    const pa = a[r];
    const pb = b[c];
    if (pa === undefined) return 0;
    if (pb === undefined) return UNUSED * UNUSED;
    return (pa[0] - pb[0]) ** 2 + (pa[1] - pb[1]) ** 2;
  };
  return assignMinCost(n, cost).slice(0, islands.length).map((c) => body[c] ?? null);
}

/** Reading order: top to bottom by centre (rows overlap when the centres are within half the shorter height), then x. */
const reading = (p: Island, q: Island): number => {
  const dy = p.y + p.h / 2 - (q.y + q.h / 2);
  return Math.abs(dy) > Math.min(p.h, q.h) / 2 ? dy : p.x - q.x;
};

/** The attachment for a left-over island: it lies beside the matched body, on the side an attachment is held. */
function attachmentFor(island: Island, used: readonly Island[], body: readonly PlacedPart[], free: PlacedPart[]): PlacedPart | null {
  if (used.length === 0) return null;
  const x0 = Math.min(...used.map((i) => i.x));
  const x1 = Math.max(...used.map((i) => i.x + i.w));
  const within = island.y >= Math.min(...used.map((i) => i.y)) && island.y + island.h <= Math.max(...used.map((i) => i.y + i.h));
  const side = island.x >= x1 ? 1 : island.x + island.w <= x0 ? -1 : 0;
  if (!within || side === 0) return null;
  const mid = body.reduce((s, p) => s + partCentre(p)[0], 0) / Math.max(1, body.length);
  const k = free.findIndex((p) => Math.sign(p.pivotScreen[0] - mid) === side);
  return k < 0 ? null : (free.splice(k, 1)[0] ?? null);
}

/**
 * Proposes a target per island, islands in reading order. Body parts (one per node, `primaryPartsOf`) are matched by
 * layout: island centres and part centres are each normalised to their own box and the cheapest one-to-one assignment
 * wins (head above torso above legs, left and right by x). A left-over island lying beside the matched body (outside
 * its columns, within its height) on the side where the puppet holds an attachment (a weapon in the rest pose, placed
 * with `withAttachments`) gets that attachment; every other left-over island is unused.
 */
export function proposeTargets(islands: readonly Island[], placement: RestPlacement): IslandTarget[] {
  const ordered = [...islands].sort(reading);
  const body = primaryPartsOf(placement.parts.filter((p) => !p.attachment));
  const parts = ordered.length === 0 || body.length === 0 ? ordered.map(() => null) : assignBody(ordered, body);
  const used = ordered.filter((_, k) => parts[k] !== null);
  const free = placement.parts.filter((p) => p.attachment);
  return ordered.map((island, k) => {
    const part = parts[k] ?? attachmentFor(island, used, body, free);
    return { island, part, pivot: pivotProposal(island, part) };
  });
}
